import { useState, useCallback, useEffect, useMemo } from 'react';
import { Stack, Group, Text, Button, Card, Box } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { useNavigate } from '@tanstack/react-router';
import { IconPlus, IconListTree } from '@tabler/icons-react';
import type { ListlistDto } from '@lib2/model/listlist/ListlistDto';
import type { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import { useAddChild, useRemoveChild, useSaveChildOrder } from '../useAppContentMutations';
import { AppContentUtils } from '../AppContentUtils';
import { SortableChildItem } from './SortableChildItem';
import { AddChildDialog } from './AddChildDialog';

interface OrderedChildrenEditorProps {
  parentLlId: number;
  parentType: ListlistTypeId;
  orderedChildLlIds: number[];
  allAvailableChildren: ListlistDto[];
  childLookup: Map<number, ListlistDto>;
  childDetailRoute: string;
}

export function OrderedChildrenEditor({
  parentLlId,
  parentType,
  orderedChildLlIds,
  allAvailableChildren,
  childLookup,
  childDetailRoute,
}: OrderedChildrenEditorProps) {
  const navigate = useNavigate();
  const addMutation = useAddChild();
  const removeMutation = useRemoveChild();
  const saveOrderMutation = useSaveChildOrder();

  const childTypeLabel = AppContentUtils.getChildTypeLabel(parentType);
  const childTypeLabelPlural = AppContentUtils.getChildTypeLabelPlural(parentType);

  // Local draft state for ordering (staged Save/Discard)
  const [draftOrder, setDraftOrder] = useState<number[]>(orderedChildLlIds);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Sync draft when server data changes
  useEffect(() => {
    setDraftOrder(orderedChildLlIds);
  }, [orderedChildLlIds]);

  // Check if draft differs from server state
  const hasOrderChanges = useMemo(() => {
    if (draftOrder.length !== orderedChildLlIds.length) return false;
    return draftOrder.some((id, i) => id !== orderedChildLlIds[i]);
  }, [draftOrder, orderedChildLlIds]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = draftOrder.indexOf(active.id as number);
      const newIndex = draftOrder.indexOf(over.id as number);

      if (oldIndex !== -1 && newIndex !== -1) {
        setDraftOrder(arrayMove(draftOrder, oldIndex, newIndex));
      }
    },
    [draftOrder],
  );

  const handleAdd = useCallback(
    (childLlId: number) => {
      addMutation.mutate(
        {
          parentLLId: parentLlId,
          childId: { childLLId: childLlId, childListId: null },
        },
        {
          onSuccess: () => {
            setAddDialogOpen(false);
            notifications.show({
              title: 'Added',
              message: `${childTypeLabel} added successfully.`,
              color: 'green',
            });
          },
          onError: (err) => {
            notifications.show({ title: 'Error', message: err.message, color: 'red' });
          },
        },
      );
    },
    [parentLlId, addMutation, childTypeLabel],
  );

  const handleRemove = useCallback(
    (childLlId: number) => {
      const child = childLookup.get(childLlId);
      const childTitle = child?.llr.title ?? 'this item';

      modals.openConfirmModal({
        title: `Remove ${childTypeLabel}`,
        children: (
          <Text size="sm">
            Remove &ldquo;{childTitle}&rdquo; from this list? The {childTypeLabel.toLowerCase()} itself
            will not be deleted.
          </Text>
        ),
        labels: { confirm: 'Remove', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          removeMutation.mutate(
            {
              parentLLId: parentLlId,
              childId: { childLLId: childLlId, childListId: null },
            },
            {
              onSuccess: () => {
                // Remove from local draft immediately
                setDraftOrder((prev) => prev.filter((id) => id !== childLlId));
                notifications.show({
                  title: 'Removed',
                  message: `${childTypeLabel} removed.`,
                  color: 'green',
                });
              },
              onError: (err) => {
                notifications.show({ title: 'Error', message: err.message, color: 'red' });
              },
            },
          );
        },
      });
    },
    [parentLlId, childLookup, childTypeLabel, removeMutation],
  );

  const handleSaveOrder = useCallback(() => {
    saveOrderMutation.mutate(
      {
        parentLLId: parentLlId,
        childIds: draftOrder.map((llId) => ({ childLLId: llId, childListId: null })),
      },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Saved',
            message: `${childTypeLabelPlural} order saved.`,
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
        },
      },
    );
  }, [parentLlId, draftOrder, childTypeLabelPlural, saveOrderMutation]);

  const handleDiscardOrder = useCallback(() => {
    setDraftOrder(orderedChildLlIds);
  }, [orderedChildLlIds]);

  const handleNavigate = useCallback(
    (childLlId: number) => {
      const displayId = AppContentUtils.getDisplayId(childLlId);
      navigate({ to: `${childDetailRoute}/${displayId}` as string });
    },
    [navigate, childDetailRoute],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">
          {childTypeLabelPlural} ({draftOrder.length})
        </Text>
        <Group gap="sm">
          {hasOrderChanges && (
            <>
              <Button
                variant="default"
                size="sm"
                onClick={handleDiscardOrder}
              >
                Discard Order
              </Button>
              <Button
                size="sm"
                onClick={handleSaveOrder}
                loading={saveOrderMutation.isPending}
              >
                Save Order
              </Button>
            </>
          )}
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
          >
            Add {childTypeLabel}
          </Button>
        </Group>
      </Group>

      {draftOrder.length === 0 ? (
        <Card withBorder p="xl">
          <Stack align="center" gap="sm">
            <IconListTree size={36} stroke={1.5} opacity={0.4} />
            <Text size="sm" c="dimmed" ta="center">
              No {childTypeLabelPlural.toLowerCase()} assigned yet.
            </Text>
            <Button
              variant="light"
              leftSection={<IconPlus size={14} />}
              size="sm"
              onClick={() => setAddDialogOpen(true)}
            >
              Add {childTypeLabel}
            </Button>
          </Stack>
        </Card>
      ) : (
        <Card withBorder p="sm">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={draftOrder}
              strategy={verticalListSortingStrategy}
            >
              <Stack gap="xs">
                {draftOrder.map((childLlId, index) => {
                  const child = childLookup.get(childLlId);
                  return (
                    <SortableChildItem
                      key={childLlId}
                      id={childLlId}
                      index={index}
                      title={child?.llr.title ?? `Unknown (${childLlId})`}
                      displayId={AppContentUtils.getDisplayId(childLlId)}
                      onRemove={handleRemove}
                      onNavigate={handleNavigate}
                    />
                  );
                })}
              </Stack>
            </SortableContext>
          </DndContext>
          <Text size="xs" c="dimmed" mt="xs" ta="center">
            Drag to reorder
          </Text>
        </Card>
      )}

      <AddChildDialog
        opened={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdd={handleAdd}
        availableChildren={allAvailableChildren}
        existingChildIds={draftOrder}
        childTypeLabel={childTypeLabel}
        isPending={addMutation.isPending}
      />
    </Stack>
  );
}
